import { useState } from "react";
import { api, API } from "@/lib/api";
import AiAssistantModal from "@/components/AiAssistantModal";
import ImageStudioModal from "@/components/ImageStudioModal";
import SubItemCard from "@/components/SubItemCard";
import SubItemAIModal from "@/components/SubItemAIModal";
import AiChatPanel from "@/components/AiChatPanel";
import DossierPresentation from "@/components/DossierPresentation";
import SectionQualityBadge from "@/components/SectionQualityBadge";
import DossierGlobalQualityPanel from "@/components/DossierGlobalQualityPanel";
import SlidePreview, { LAYOUT_OPTIONS } from "@/components/SlidePreview";
import LayoutGalleryModal from "@/components/LayoutGalleryModal";
import AIDetectorModal from "@/components/AIDetectorModal";

const SECTION_HINTS = {
  titulo: "El nombre del proyecto. Puede cambiar durante el desarrollo.",
  portada: "Una imagen + 1-3 líneas que capten el tono.",
  logline: "1-2 frases. Protagonista + conflicto + apuesta dramática.",
  sinopsis: "3-5 párrafos. Qué, quién, dónde, cuándo.",
  nota_intenciones: "Por qué cuento esta historia. 1ª persona, sobrio.",
  vinculo: "¿Por qué yo? Honestidad por encima de elocuencia.",
  contexto: "Marco social, geográfico, histórico. Datos concretos.",
  personajes: "Crea una ficha por personaje (principal, secundario, colectivo, lugar…). La IA toma todo el dossier como contexto.",
  tratamiento: "El cómo: cámara, luz, sonido, ritmo.",
  referencias: "Crea una ficha por obra de referencia. La IA propone obras coherentes con tu tratamiento y contexto.",
  biofilmo: "Director/a + productora. Obra previa relevante.",
  cartas: "Cartas de coproducción o apoyo institucional.",
  produccion: "Enlace al presupuesto, financiación y calendario.",
};

// Tips-recordatorio (cita) por sección — se muestran plegados por defecto
// bajo la cabecera de la sección al pulsar el botón "💡 Consejo". Preguntas
// clave que ayudan al autor a auto-evaluar su texto antes de darlo por bueno.
// Añadir más tips es tan simple como registrar una nueva clave aquí.
const SECTION_TIPS = {
  sinopsis: {
    title: "Cómo saber si tu sinopsis está bien escrita",
    body: "Una buena forma de saber que la sinopsis está correctamente escrita es plantearse si puede responder a preguntas del tipo: ¿Queda claro el objetivo del protagonista? ¿Cuál es el clímax? ¿Es impactante? ¿Cuáles son los rasgos principales del protagonista? ¿Qué queremos explicar con esta historia? ¿Vale la pena? ¿Generará conflicto el problema planteado?",
  },
};

const STRUCTURED_KEYS = ["personajes", "referencias", "biofilmo", "cartas"];

const EMPTY_HELP = {
  personajes: "un personaje",
  referencias: "una referencia",
  biofilmo: "una ficha de equipo (director/a, productor/a, DOP, montaje…)",
  cartas: "una carta de intención (coproducción, distribución, apoyo institucional…)",
};

const IMG_LABEL = {
  personajes: "Personaje",
  referencias: "Referencia",
  biofilmo: "Equipo",
  cartas: "Carta",
};

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export default function DossierTab({ project, projectId, onChange, onSave }) {
  const [aiSection, setAiSection] = useState(null);    // {key, label, question}
  const [imgSection, setImgSection] = useState(null);
  const [structure, setStructure] = useState(null);
  const [subAiSection, setSubAiSection] = useState(null);   // {key, label}
  const [subImgTarget, setSubImgTarget] = useState(null);   // {sectionKey, itemId}
  const [chatTarget, setChatTarget] = useState(null);       // {key, label} -> chat to improve section.text
  const [presenting, setPresenting] = useState(false);
  const [aiDetectorOpen, setAiDetectorOpen] = useState(false);
  // Set de keys de sección cuyo tip está desplegado. Cerrado por defecto
  // para no ocupar espacio; el usuario abre solo el que necesita consultar.
  const [openTips, setOpenTips] = useState(() => new Set());
  const toggleTip = (key) => {
    setOpenTips((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // load structure once for fallback
  if (!structure) {
    api.get("/icaa/dossier-structure").then((r) => setStructure(r.data));
  }
  const sections = project.dossier?.sections || [];
  const sectionByKey = Object.fromEntries(sections.map((s) => [s.key, s]));

  const updateSection = (key, patch) => {
    const next = sections.map((s) => (s.key === key ? { ...s, ...patch } : s));
    onChange({ ...project, dossier: { sections: next } });
  };
  const setLayout = (key, layout) => {
    updateSection(key, { layout });
    setTimeout(onSave, 200);
  };
  const renameSection = (key) => {
    const sec = sectionByKey[key];
    const name = window.prompt("Nuevo nombre del módulo:", sec?.label || "");
    if (!name || !name.trim() || name.trim() === sec.label) return;
    updateSection(key, { label: name.trim() });
    setTimeout(onSave, 200);
  };
  const removeSection = (key) => {
    const sec = sectionByKey[key];
    const hasContent = !!(sec?.text?.trim() || (sec?.items || []).length || (sec?.images || []).length);
    const msg = hasContent
      ? `Vas a eliminar el módulo "${sec.label}" y todo su contenido (texto, fichas e imágenes).\n\n¿Estás seguro?`
      : `¿Eliminar el módulo "${sec.label}"?`;
    if (!window.confirm(msg)) return;
    const next = sections.filter((s) => s.key !== key);
    onChange({ ...project, dossier: { sections: next } });
    setTimeout(onSave, 200);
  };
  const moveSection = (key, dir) => {
    const idx = sections.findIndex((s) => s.key === key);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...project, dossier: { sections: next } });
    setTimeout(onSave, 200);
  };
  const addCustomSection = () => {
    const name = window.prompt("Nombre del nuevo módulo (texto libre):", "");
    if (!name || !name.trim()) return;
    const slug = name.trim()
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || `custom-${Date.now().toString(36)}`;
    const key = sections.some((s) => s.key === slug) ? `${slug}-${Date.now().toString(36).slice(-4)}` : slug;
    const next = [...sections, { key, label: name.trim(), text: "", images: [], items: [], notes: "", layout: "" }];
    onChange({ ...project, dossier: { sections: next } });
    setTimeout(onSave, 200);
  };
  const addImage = (key, img) => {
    const s = sectionByKey[key];
    const next = { ...s, images: [...(s.images || []), { ...img, id: `${Date.now()}` }] };
    updateSection(key, { images: next.images });
  };
  const removeImage = (key, imgId) => {
    const s = sectionByKey[key];
    updateSection(key, { images: (s.images || []).filter((i) => i.id !== imgId) });
  };

  // ----- structured sub-items helpers -----
  const items = (key) => sectionByKey[key]?.items || [];
  const setItems = (key, next) => updateSection(key, { items: next });

  const addBlankItem = (key) => {
    setItems(key, [...items(key), { id: uid(), title: "", meta: "", body: "", image_url: "" }]);
    setTimeout(onSave, 200);
  };
  const updateItem = (key, id, next) => {
    setItems(key, items(key).map((it) => (it.id === id ? next : it)));
  };
  const removeItem = (key, id) => {
    setItems(key, items(key).filter((it) => it.id !== id));
    setTimeout(onSave, 200);
  };
  const appendAiItem = (key, draft) => {
    setItems(key, [...items(key), { id: uid(), ...draft, image_url: "" }]);
    setSubAiSection(null);
    setTimeout(onSave, 200);
  };
  const attachImageToItem = (key, itemId, img) => {
    setItems(key, items(key).map((it) => (it.id === itemId ? { ...it, image_url: img.url } : it)));
    setSubImgTarget(null);
    setTimeout(onSave, 200);
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8" data-testid="dossier-section">
      {/* Toolbar superior con "Modo presentación" */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gb-paper bg-gb-surface px-4 py-3">
        <div>
          <p className="gb-eyebrow">Dossier · {sections.length} secciones</p>
          <p className="mt-0.5 text-xs text-gb-muted">Edita las secciones abajo o pulsa &quot;Modo presentación&quot; para enseñarlo en pantalla completa (16:9).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAiDetectorOpen(true)} data-testid="dossier-ai-detector-btn"
                  title="Verifica si un texto ha sido escrito por IA o a mano"
                  className="inline-flex items-center gap-1.5 rounded-full border border-stone-400 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-700 transition hover:border-[#941620] hover:text-[#941620]">
            🤖 Detector IA / Humano
          </button>
          <button onClick={() => setPresenting(true)} data-testid="dossier-present-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-gb-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-gb-red">
            ▸ Modo presentación
          </button>
        </div>
      </div>

      {/* Panel global de control de calidad — agrega los warnings de todas las
          secciones, da total de slides estimados, secciones vacías, y permite
          saltar a la sección concreta que tiene el aviso. */}
      <div className="mb-5">
        <DossierGlobalQualityPanel sections={sections} />
      </div>

      {sections.map((sec, idx) => {
        const meta = structure?.find((s) => s.key === sec.key);
        const structured = STRUCTURED_KEYS.includes(sec.key);
        return (
          <article key={sec.key} className="mb-6 overflow-hidden rounded-2xl border border-stone-300/70 bg-white"
                   data-testid={`section-${sec.key}`}>
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 bg-[#F1EEE5]/40 px-4 py-3 sm:px-6">
              <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                <span className="font-mono text-[11px] text-[#26775E]">{String(idx + 1).padStart(2, "0")}</span>
                <h3 className="truncate font-serif text-base text-stone-900 sm:text-lg">{sec.label}</h3>
                <SectionQualityBadge section={sec} />
                <p className="hidden text-xs italic text-stone-500 lg:block">{meta?.question || SECTION_HINTS[sec.key]}</p>
              </div>
              <div className="flex items-center gap-1">
                {/* Module actions: rename / move / delete */}
                <div className="mr-2 flex items-center gap-0.5 rounded-full border border-stone-300 bg-white/60 px-1 py-0.5">
                  <button onClick={() => moveSection(sec.key, -1)} disabled={idx === 0}
                          data-testid={`section-up-${sec.key}`}
                          title="Mover arriba"
                          className="rounded-full px-1.5 text-[12px] text-stone-500 hover:text-[#26775E] disabled:opacity-30">↑</button>
                  <button onClick={() => moveSection(sec.key, +1)} disabled={idx === sections.length - 1}
                          data-testid={`section-down-${sec.key}`}
                          title="Mover abajo"
                          className="rounded-full px-1.5 text-[12px] text-stone-500 hover:text-[#26775E] disabled:opacity-30">↓</button>
                  <button onClick={() => renameSection(sec.key)}
                          data-testid={`section-rename-${sec.key}`}
                          title="Renombrar módulo"
                          className="rounded-full px-1.5 text-[12px] text-stone-500 hover:text-[#26775E]">✎</button>
                  <button onClick={() => removeSection(sec.key)}
                          data-testid={`section-delete-${sec.key}`}
                          title="Eliminar módulo"
                          className="rounded-full px-1.5 text-[12px] text-stone-500 hover:text-red-600">🗑</button>
                </div>
                {SECTION_TIPS[sec.key] && (
                  <button onClick={() => toggleTip(sec.key)}
                          data-testid={`section-tip-toggle-${sec.key}`}
                          title="Ver consejo para esta sección"
                          aria-expanded={openTips.has(sec.key)}
                          className={`mr-2 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest transition ${
                            openTips.has(sec.key)
                              ? "border-amber-500 bg-amber-100 text-amber-900"
                              : "border-stone-400 bg-white text-stone-700 hover:border-amber-500 hover:text-amber-800"
                          }`}>
                    💡 Consejo
                  </button>
                )}
                {structured ? (
                  <>
                    <button onClick={() => setSubAiSection({ key: sec.key, label: sec.label })}
                            data-testid={`subitem-add-ai-${sec.key}`}
                            className="rounded-full bg-stone-900 px-3 py-1 text-[10px] uppercase tracking-widest text-[#FAFAF6] hover:bg-[#26775E]">
                      ✨ Añadir con Claude
                    </button>
                    <button onClick={() => addBlankItem(sec.key)}
                            data-testid={`subitem-add-blank-${sec.key}`}
                            className="rounded-full border border-stone-400 px-3 py-1 text-[10px] uppercase tracking-widest text-stone-700 hover:border-[#26775E] hover:text-[#26775E]">
                      + En blanco
                    </button>
                  </>
                ) : (
                  <>
                    {sec.key !== "produccion" && (
                      <button onClick={() => setAiSection({ key: sec.key, label: sec.label, question: meta?.question || "" })}
                              data-testid={`ai-btn-${sec.key}`}
                              className="rounded-full bg-stone-900 px-3 py-1 text-[10px] uppercase tracking-widest text-[#FAFAF6] hover:bg-[#26775E]">
                        ✨ Generar
                      </button>
                    )}
                    {sec.key !== "produccion" && (
                      <button onClick={() => setChatTarget({ key: sec.key, label: sec.label })}
                              data-testid={`chat-btn-${sec.key}`}
                              className="rounded-full border border-[#26775E] px-3 py-1 text-[10px] uppercase tracking-widest text-[#26775E] hover:bg-[#26775E] hover:text-white">
                        💬 Mejorar con IA
                      </button>
                    )}
                    {sec.key !== "produccion" && sec.key !== "cartas" && (
                      <button onClick={() => setImgSection({ key: sec.key, label: sec.label })}
                              data-testid={`img-btn-${sec.key}`}
                              className="rounded-full border border-stone-400 px-3 py-1 text-[10px] uppercase tracking-widest text-stone-700 hover:border-[#26775E] hover:text-[#26775E]">
                        + Imagen
                      </button>
                    )}
                  </>
                )}
              </div>
            </header>
            {openTips.has(sec.key) && SECTION_TIPS[sec.key] && (
              <div className="border-b border-amber-200 bg-amber-50/70 px-4 py-3 sm:px-6" data-testid={`section-tip-${sec.key}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-900">
                  💡 {SECTION_TIPS[sec.key].title}
                </p>
                <p className="mt-1.5 font-serif text-[13px] italic leading-relaxed text-stone-800">
                  &ldquo;{SECTION_TIPS[sec.key].body}&rdquo;
                </p>
              </div>
            )}

            {structured ? (
              // ---------- Structured cards view (personajes / referencias) ----------
              <div className="space-y-4 p-6" data-testid={`subitems-${sec.key}`}
                   onBlur={onSave}>
                <SlideLayoutPicker section={sec} project={project} onChange={(v) => setLayout(sec.key, v)} />
                {(sec.items || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500"
                     data-testid={`subitems-empty-${sec.key}`}>
                    Aún no hay fichas. Pulsa <strong>✨ Añadir con Claude</strong> para que la IA proponga {EMPTY_HELP[sec.key] || "una ficha"} basada en todo lo que ya has escrito en el dossier.
                  </p>
                )}
                {(sec.items || []).map((it) => (
                  <SubItemCard key={it.id}
                               item={it}
                               sectionKey={sec.key}
                               onChange={(next) => updateItem(sec.key, it.id, next)}
                               onRemove={() => removeItem(sec.key, it.id)}
                               onPickImage={() => setSubImgTarget({ sectionKey: sec.key, itemId: it.id })} />
                ))}
              </div>
            ) : (
              // ---------- Classic single-textarea view ----------
              <div className="p-4 sm:p-6 space-y-5">
                <SlideLayoutPicker section={sec} project={project} onChange={(v) => setLayout(sec.key, v)} />
                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-[1fr,260px]">
                <textarea
                  value={sec.text || ""}
                  onChange={(e) => updateSection(sec.key, { text: e.target.value })}
                  onBlur={onSave}
                  data-testid={`text-${sec.key}`}
                  rows={sec.key === "titulo" ? 1 : sec.key === "logline" || sec.key === "portada" ? 3 : 10}
                  placeholder={SECTION_HINTS[sec.key]}
                  className="w-full resize-y rounded-md border border-stone-200 bg-[#FBFAF4] p-4 font-serif text-[15px] leading-relaxed text-stone-900 outline-none focus:border-[#26775E] focus:bg-white"
                />
                <div data-testid={`images-${sec.key}`}>
                  <p className="mb-2 text-[10px] uppercase tracking-widest text-stone-500">
                    Imágenes ({sec.images?.length || 0})
                  </p>
                  <div className="space-y-2">
                    {(sec.images || []).map((img) => (
                      <div key={img.id} className="group relative overflow-hidden rounded-md ring-1 ring-stone-200">
                        <img src={img.url.startsWith("data:") ? img.url : `${API.replace('/api','')}${img.url}`}
                             alt="" className="aspect-video w-full object-cover" />
                        <button onClick={() => removeImage(sec.key, img.id)}
                                data-testid={`remove-img-${img.id}`}
                                className="absolute right-1 top-1 rounded-full bg-stone-900/70 px-2 text-xs text-white opacity-0 transition group-hover:opacity-100">×</button>
                        {img.source === "ai" && (
                          <span className="absolute bottom-1 left-1 rounded bg-[#26775E]/85 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-white">
                            IA
                          </span>
                        )}
                      </div>
                    ))}
                    {!sec.images?.length && (
                      <p className="rounded border border-dashed border-stone-300 p-4 text-center text-[11px] text-stone-400">
                        Sin imágenes aún
                      </p>
                    )}
                  </div>
                </div>
                </div>
              </div>
            )}
          </article>
        );
      })}

      {/* Add custom module */}
      <div className="mb-6 rounded-2xl border-2 border-dashed border-stone-300 bg-white/40 p-5 text-center">
        <p className="font-display text-base text-stone-700">¿Necesitas un módulo más?</p>
        <p className="mt-1 text-[12px] text-stone-500">
          Crea uno nuevo (texto libre) o restáuralo escribiendo el mismo nombre de uno que borraste.
        </p>
        <button onClick={addCustomSection} data-testid="add-section-btn"
                className="mt-3 rounded-full bg-stone-900 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#26775E]">
          + Añadir módulo
        </button>
      </div>

      {aiSection && (
        <AiAssistantModal
          projectId={projectId}
          section={aiSection}
          currentText={sectionByKey[aiSection.key]?.text || ""}
          onClose={() => setAiSection(null)}
          onApply={(text, mode) => {
            const current = sectionByKey[aiSection.key]?.text || "";
            const next = mode === "replace" ? text : current ? current + "\n\n" + text : text;
            updateSection(aiSection.key, { text: next });
            setAiSection(null);
            setTimeout(onSave, 200);
          }}
        />
      )}

      {imgSection && (
        <ImageStudioModal
          projectId={projectId}
          sectionLabel={imgSection.label}
          suggestPrompt={`Imagen para la sección "${imgSection.label}" del documental "${project.title}". ${project.locations ? "Localización: " + project.locations + "." : ""}`}
          onAdd={(img) => { addImage(imgSection.key, img); setTimeout(onSave, 200); }}
          onClose={() => setImgSection(null)}
        />
      )}

      {subAiSection && (
        <SubItemAIModal
          projectId={projectId}
          sectionKey={subAiSection.key}
          existingItems={items(subAiSection.key)}
          onClose={() => setSubAiSection(null)}
          onApply={(draft) => appendAiItem(subAiSection.key, draft)}
        />
      )}

      {subImgTarget && (
        <ImageStudioModal
          projectId={projectId}
          sectionLabel={IMG_LABEL[subImgTarget.sectionKey] || "Ficha"}
          suggestPrompt={(() => {
            const it = (sectionByKey[subImgTarget.sectionKey]?.items || []).find((x) => x.id === subImgTarget.itemId);
            const k = subImgTarget.sectionKey;
            const base = k === "personajes"
              ? `Retrato cinematográfico de ${it?.title || "un personaje"} para el documental "${project.title}". ${it?.body?.slice(0, 200) || ""}`
              : k === "referencias"
              ? `Frame visual evocador inspirado en "${it?.title || ""}" (${it?.meta || ""}). ${it?.body?.slice(0, 200) || ""}`
              : k === "biofilmo"
              ? `Retrato profesional sobrio de ${it?.title || ""} (${it?.meta || ""}). Para dossier audiovisual de "${project.title}".`
              : `Logo o identidad visual representativa de ${it?.title || ""} para "${project.title}".`;
            return base + (project.locations ? ` Localización del proyecto: ${project.locations}.` : "");
          })()}
          onAdd={(img) => attachImageToItem(subImgTarget.sectionKey, subImgTarget.itemId, img)}
          onClose={() => setSubImgTarget(null)}
        />
      )}

      {chatTarget && (
        <AiChatPanel
          projectId={projectId}
          sectionLabel={`Dossier · ${chatTarget.label}`}
          value={sectionByKey[chatTarget.key]?.text || ""}
          onApply={(newText, mode) => {
            const current = sectionByKey[chatTarget.key]?.text || "";
            const next = mode === "append" ? (current ? current + "\n\n" + newText : newText) : newText;
            updateSection(chatTarget.key, { text: next });
            setTimeout(onSave, 200);
          }}
          onClose={() => setChatTarget(null)}
        />
      )}

      {presenting && (
        <DossierPresentation
          project={project}
          onClose={() => setPresenting(false)}
          onSectionUpdate={(nextProject) => onChange(nextProject)}
        />
      )}
      <AIDetectorModal
        open={aiDetectorOpen}
        onClose={() => setAiDetectorOpen(false)}
      />
    </section>
  );
}

// ===== SlideLayoutPicker: selector de plantilla + mini-preview 16:9 ============
function SlideLayoutPicker({ section, project, onChange }) {
  const current = section.layout || "";
  const [galleryOpen, setGalleryOpen] = useState(false);
  return (
    <div className="rounded-xl border border-stone-200 bg-[#FBFAF4] p-3"
         data-testid={`slide-layout-picker-${section.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
            Plantilla 16:9 · Modo presentación
          </p>
          <p className="mt-1 text-[11px] text-stone-500">
            Elige cómo se renderiza esta sección al presentar y al exportar a PPTX. «Automático» deja que el sistema decida según el contenido.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              value={current}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`slide-layout-select-${section.key}`}
              className="w-full max-w-xs rounded border border-stone-300 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-700 outline-none focus:border-[#941620]"
            >
              {LAYOUT_OPTIONS.map((o) => (
                <option key={o.value || "auto"} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setGalleryOpen(true)}
              data-testid={`layout-gallery-open-${section.key}`}
              title="Abrir galería visual de plantillas"
              className="rounded border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-700 transition hover:border-[#941620] hover:text-[#941620]"
            >
              ⚏ Galería
            </button>
          </div>
        </div>
        <div className="w-64 shrink-0">
          <SlidePreview section={section} project={project} layout={current} />
        </div>
      </div>
      <LayoutGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        section={section}
        project={project}
        currentLayout={current}
        onPickLayout={onChange}
      />
    </div>
  );
}
